import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useFeedback } from '../../contexts/FeedbackContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import platformStatsService from '../../services/platformStatsService';
import reportService from '../../services/reportService';
import paymentService from '../../services/paymentService';

// Icons
import {
  Users as UsersIcon,
  Megaphone,
  CreditCard,
  Flag,
  TrendingUp,
  ArrowRight,
} from 'lucide-react';

const formatCurrency = (value) => {
  if (value >= 1000000) return `₱${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `₱${(value / 1000).toFixed(1)}K`;
  return `₱${value.toLocaleString('en-PH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Stat card — clean, minimal, shadcn-style (matches brand dashboard)
const StatCard = ({ label, value, sublabel, icon: Icon, trend }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-sm transition-shadow duration-200">
    <div className="flex items-center justify-between">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
      {Icon && <Icon className="h-5 w-5 text-gray-400" />}
    </div>
    <div className="mt-2 flex items-baseline gap-2">
      <span className="text-2xl font-bold text-gray-900">{value}</span>
      {trend !== undefined && trend !== null && (
        <span className={`text-xs font-medium ${trend > 0 ? 'text-green-600' : 'text-gray-400'}`}>
          {trend > 0 ? `+${trend}%` : ''}
        </span>
      )}
    </div>
    {sublabel && <p className="mt-1 text-xs text-gray-400">{sublabel}</p>}
  </div>
);

const AdminDashboard = () => {
  const { currentUser } = useAuth();
  const { showError } = useFeedback();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalBrands: 0,
    totalInfluencers: 0,
    totalCampaigns: 0,
    activeCampaigns: 0,
    completedCampaigns: 0,
    totalPayments: 0,
    pendingReports: 0,
    growth: {},
  });

  const [recentUsers, setRecentUsers] = useState([]);
  const [recentCampaigns, setRecentCampaigns] = useState([]);
  const [recentReports, setRecentReports] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch platform stats, pending reports count, report stats, and payments in parallel
        const [platformStats, pendingReportsCount, paymentsSnap, usersSnap, campaignsSnap, reportsData] = await Promise.all([
          platformStatsService.getPlatformStats(),
          reportService.getPendingReportsCount(),
          getDocs(collection(db, 'payments')),
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'campaigns')),
          reportService.getAllReports(),
        ]);

        // Calculate total payments processed (completed)
        const totalPayments = paymentsSnap.docs.reduce((sum, doc) => {
          const data = doc.data();
          if (data.status === 'completed') {
            return sum + (Number(data.amount) || 0);
          }
          return sum;
        }, 0);

        setStats({
          totalUsers: platformStats.totalUsers,
          totalBrands: platformStats.totalBrands,
          totalInfluencers: platformStats.totalInfluencers,
          totalCampaigns: platformStats.totalCampaigns,
          activeCampaigns: platformStats.activeCampaigns,
          completedCampaigns: platformStats.completedCampaigns,
          totalPayments,
          pendingReports: pendingReportsCount,
          growth: platformStats.growth || {},
        });

        // Recent users (sorted by createdAt desc, limit 5)
        const users = usersSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        users.sort((a, b) => {
          const aTime = a.createdAt?.seconds || (a.createdAt instanceof Date ? a.createdAt.getTime() / 1000 : 0);
          const bTime = b.createdAt?.seconds || (b.createdAt instanceof Date ? b.createdAt.getTime() / 1000 : 0);
          return bTime - aTime;
        });
        setRecentUsers(users.slice(0, 5));

        // Recent campaigns (sorted by createdAt desc, limit 5)
        const campaigns = campaignsSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        campaigns.sort((a, b) => {
          const aTime = a.createdAt?.seconds || (a.createdAt instanceof Date ? a.createdAt.getTime() / 1000 : 0);
          const bTime = b.createdAt?.seconds || (b.createdAt instanceof Date ? b.createdAt.getTime() / 1000 : 0);
          return bTime - aTime;
        });
        setRecentCampaigns(campaigns.slice(0, 5));

        // Recent reports (limit 5)
        setRecentReports(reportsData.slice(0, 5));
      } catch (err) {
        console.error('Error fetching admin dashboard data:', err);
        setError('Failed to load dashboard data. Please try again.');
        showError('Error', 'Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [currentUser?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" color="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Admin Dashboard</h2>
          <p className="text-gray-600">Platform-wide overview and statistics</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-1">Something went wrong</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">Admin Dashboard</h2>
        <p className="text-gray-600">Platform-wide overview and statistics</p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Total Users"
          value={stats.totalUsers}
          sublabel={`${stats.totalBrands} brands · ${stats.totalInfluencers} influencers`}
          icon={UsersIcon}
          trend={(stats.growth?.brands || 0) + (stats.growth?.influencers || 0) > 0 ? Math.round(((stats.growth?.brands || 0) + (stats.growth?.influencers || 0)) / 2) : undefined}
        />
        <StatCard
          label="Total Campaigns"
          value={stats.totalCampaigns}
          sublabel={`${stats.activeCampaigns} active · ${stats.completedCampaigns} completed`}
          icon={Megaphone}
          trend={stats.growth?.campaigns}
        />
        <StatCard
          label="Payments Processed"
          value={formatCurrency(stats.totalPayments)}
          sublabel="Completed transactions"
          icon={CreditCard}
        />
        <StatCard
          label="Pending Reports"
          value={stats.pendingReports}
          sublabel="Flagged content awaiting review"
          icon={Flag}
        />
      </div>

      {/* Pending Reports Section */}
      {stats.pendingReports > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Flag className="h-5 w-5 text-amber-600" />
              <div>
                <p className="text-sm font-medium text-amber-900">
                  {stats.pendingReports} pending report{stats.pendingReports !== 1 ? 's' : ''} need attention
                </p>
                <p className="text-xs text-amber-700">Review flagged content and take appropriate action</p>
              </div>
            </div>
            <Link
              to="/admin/reports"
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-amber-700 bg-amber-100 hover:bg-amber-200 transition-colors"
            >
              Review Reports
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
        </div>
      )}

      {/* Recent Activity Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Users */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-800">Recent Users</h3>
            <Link to="/admin/users" className="text-sm font-medium text-primary hover:text-primary/80">
              View all
            </Link>
          </div>
          {recentUsers.length > 0 ? (
            <div className="space-y-3">
              {recentUsers.map((user) => (
                <div key={user.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {user.companyName || user.displayName || user.name || 'Unnamed'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      user.userType === 'brand' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                    }`}>
                      {user.userType || 'unknown'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-4">No users yet</p>
          )}
        </div>

        {/* Recent Campaigns */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-800">Recent Campaigns</h3>
            <Link to="/admin/campaigns" className="text-sm font-medium text-primary hover:text-primary/80">
              View all
            </Link>
          </div>
          {recentCampaigns.length > 0 ? (
            <div className="space-y-3">
              {recentCampaigns.map((campaign) => (
                <div key={campaign.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {campaign.name || campaign.title || 'Untitled Campaign'}
                    </p>
                    <p className="text-xs text-gray-500">{formatDate(campaign.createdAt)}</p>
                  </div>
                  <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                    campaign.status === 'active' ? 'bg-green-100 text-green-800' :
                    campaign.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                    campaign.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                    campaign.status === 'paused' ? 'bg-amber-100 text-amber-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {campaign.status || 'unknown'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-4">No campaigns yet</p>
          )}
        </div>

        {/* Recent Reports */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-800">Recent Reports</h3>
            <Link to="/admin/reports" className="text-sm font-medium text-primary hover:text-primary/80">
              View all
            </Link>
          </div>
          {recentReports.length > 0 ? (
            <div className="space-y-3">
              {recentReports.map((report) => (
                <div key={report.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate capitalize">
                      {report.reason || 'other'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{report.targetType || 'unknown'}</p>
                  </div>
                  <span className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${
                    report.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                    report.status === 'resolved' ? 'bg-green-100 text-green-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {report.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 text-center py-4">No reports yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
